import { Type } from "@sinclair/typebox";
import { NonEmptyString } from "./primitives.js";

const LogLevelSchema = Type.Union([
  Type.Literal("trace"),
  Type.Literal("debug"),
  Type.Literal("info"),
  Type.Literal("warn"),
  Type.Literal("error"),
  Type.Literal("fatal"),
]);

export const LogsTailParamsSchema = Type.Object(
  {
    cursor: Type.Optional(Type.Integer({ minimum: 0 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 5000 })),
    maxBytes: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 })),
    subsystemPrefixes: Type.Optional(Type.Array(NonEmptyString, { maxItems: 32 })),
    sessionKeyPrefix: Type.Optional(Type.String({ maxLength: 256 })),
    levels: Type.Optional(Type.Array(LogLevelSchema, { maxItems: 8 })),
    needle: Type.Optional(Type.String({ maxLength: 500 })),
    sinceMs: Type.Optional(Type.Integer({ minimum: 0 })),
    includePlatform: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const LogsTailResultSchema = Type.Object(
  {
    file: NonEmptyString,
    cursor: Type.Integer({ minimum: 0 }),
    size: Type.Integer({ minimum: 0 }),
    lines: Type.Array(Type.String()),
    truncated: Type.Optional(Type.Boolean()),
    reset: Type.Optional(Type.Boolean()),
    matched: Type.Optional(Type.Integer({ minimum: 0 })),
    scanned: Type.Optional(Type.Integer({ minimum: 0 })),
  },
  { additionalProperties: false },
);

// WebChat/WebSocket-native chat methods
//
// `before` (JAISIU-3473): optional message-id cursor for upward history
// pagination. When supplied, the handler returns up to `limit` messages
// that are STRICTLY OLDER than the message with this id (chronological
// order preserved). When omitted, behaviour is unchanged: latest `limit`
// messages.
//
// `around` (JAISIU-3533): optional message-id anchor for a CENTERED
// window — when supplied, the handler returns up to `limit` messages
// with the anchor message at index `floor(limit / 2)` and roughly equal
// counts of older + newer neighbours. The intent is a fast scroll-
// restore: a single RPC lands the anchor message in the DOM so the
// restore path can place the viewport without a multi-page drain.
//
// `after` (JAISIU-3532): optional message-id cursor for downward paging —
// up to `limit` messages STRICTLY NEWER than the message with this id.
//
// The cursors are mutually exclusive; the handler applies the first one
// present in the order `around`, `before`, `after` and ignores the rest.
//
// `oldestMessageId` / `newestMessageId` let the client pin its cursors
// without scanning the returned array; `hasMoreOlder` / `hasMoreNewer`
// tell it whether to keep the upward / downward pager armed.
export const ChatHistoryParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 1000 })),
    before: Type.Optional(NonEmptyString),
    around: Type.Optional(NonEmptyString),
    after: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const ChatHistoryResultSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    sessionId: Type.Optional(NonEmptyString),
    messages: Type.Array(Type.Unknown()),
    thinkingLevel: Type.Optional(Type.String()),
    oldestMessageId: Type.Optional(Type.String()),
    hasMoreOlder: Type.Optional(Type.Boolean()),
    newestMessageId: Type.Optional(Type.String()),
    hasMoreNewer: Type.Optional(Type.Boolean()),
    /** Client run id still registered as in-flight for this session, if any. */
    activeClientRunId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

const FleetComposerModeSchema = Type.Union([
  Type.Literal("agent"),
  Type.Literal("ask"),
  Type.Literal("plan"),
  Type.Literal("debug"),
  Type.Literal("multitask"),
]);

export const ChatSendParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    message: Type.String(),
    thinking: Type.Optional(Type.String()),
    composerMode: Type.Optional(FleetComposerModeSchema),
    deliver: Type.Optional(Type.Boolean()),
    attachments: Type.Optional(Type.Array(Type.Unknown())),
    timeoutMs: Type.Optional(Type.Integer({ minimum: 0 })),
    idempotencyKey: NonEmptyString,
    // Stable, semantic client message id. Distinct from `idempotencyKey`:
    // - `idempotencyKey` is used for replay protection (same value on retry
    //   collapses to the same in-flight run).
    // - `clientMessageId` is the identity the client assigned to the user
    //   message at submit time. The server uses it as the persisted
    //   transcript entry id so the optimistic UI message can be reconciled
    //   with the canonical `chat.history` copy purely by id match (no
    //   content scan, no race with the embedded runner appending later).
    // When omitted, the server falls back to `idempotencyKey` for the
    // persisted id (legacy behavior).
    clientMessageId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const ChatSteerParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    message: Type.String(),
    idempotencyKey: Type.Optional(NonEmptyString),
    // Stable id of the steered user message. Mirrors `ChatSendParamsSchema.clientMessageId`:
    // used as the persisted transcript entry id and as the optimistic-message
    // anchor so `mergeWithPendingUserMessages` collapses the canonical copy
    // once `chat.history` arrives (idempotent on reconnect / replay).
    clientMessageId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const ChatAbortParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    runId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const ChatInjectParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    message: NonEmptyString,
    label: Type.Optional(Type.String({ maxLength: 100 })),
  },
  { additionalProperties: false },
);

export const ChatRewindParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    beforeTranscriptId: Type.Optional(NonEmptyString),
    beforeMessageIndex: Type.Optional(Type.Integer({ minimum: 0 })),
  },
  { additionalProperties: false },
);

export const ChatRegenerateParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FlociAgentsParamsSchema = Type.Object(
  {
    timeoutMs: Type.Optional(Type.Integer({ minimum: 100, maximum: 30_000 })),
  },
  { additionalProperties: false },
);

export const SubagentsListParamsSchema = Type.Object(
  {
    // Clients (Control UI, TUI panels) poll this on an interval and send
    // `requesterSessionKey: ""` whenever no session is selected yet. An empty
    // string is semantically "no filter" — the handler already falls back to
    // listing every run — but `NonEmptyString` rejected it and turned a
    // harmless poll into an INVALID_REQUEST error every ~3s. Normalize the
    // blank value to absent so validation accepts it.
    requesterSessionKey: Type.Optional(
      Type.Union([NonEmptyString, Type.Literal("")]),
    ),
  },
  { additionalProperties: false },
);

/**
 * Params for `subagents.cancelAll` — terminate every in-flight child of a
 * parent session in one call ("Stop this orchestration").
 *
 * `requesterSessionKey` selects the parent whose children are cancelled.
 * `childSessionKeys` is an optional explicit narrowing: when supplied, only
 * those children of that requester are cancelled. That keeps a cancel-all
 * scoped to the fan-out the operator is actually looking at when a parent
 * has several sequential fan-outs, instead of killing the newest one only.
 *
 * Already-settled children are skipped rather than reported as failures —
 * cancellation is idempotent, so a double-click must not error.
 */
export const SubagentsCancelAllParamsSchema = Type.Object(
  {
    requesterSessionKey: NonEmptyString,
    childSessionKeys: Type.Optional(Type.Array(NonEmptyString, { minItems: 1, maxItems: 200 })),
    /** Free-form reason surfaced in logs and the parent chat.final. */
    reason: Type.Optional(Type.String({ maxLength: 280 })),
    /**
     * When true (default), broadcast a `chat.final` to the requester session
     * so the parent thread reflects the stop without a separate fetch.
     */
    broadcast: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

/**
 * Params for `procset.status` — fetch derived (ps + log-mtime) status for
 * a batch of registry child sessionKeys. Used by the UI Inspector to
 * overlay live procset truth onto subagent cards so the operator sees
 * "running / exited / killed" without trusting stale registry rows.
 *
 * Limits: hard cap at 200 keys per call to keep the response bounded
 * (a single Inspector refresh typically has <20 cards).
 */
export const ProcsetStatusParamsSchema = Type.Object(
  {
    childSessionKeys: Type.Array(NonEmptyString, { minItems: 1, maxItems: 200 }),
  },
  { additionalProperties: false },
);

export const ChatEventSchema = Type.Object(
  {
    runId: NonEmptyString,
    sessionKey: NonEmptyString,
    seq: Type.Integer({ minimum: 0 }),
    state: Type.Union([
      Type.Literal("delta"),
      Type.Literal("final"),
      Type.Literal("aborted"),
      Type.Literal("error"),
    ]),
    message: Type.Optional(Type.Unknown()),
    errorMessage: Type.Optional(Type.String()),
    errorCode: Type.Optional(Type.String()),
    retryable: Type.Optional(Type.Boolean()),
    retryAfterMs: Type.Optional(Type.Integer({ minimum: 0 })),
    usage: Type.Optional(Type.Unknown()),
    stopReason: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
